import { HpError } from '@avantia/logging-and-errors';
import {
  HpButtonTextAndStyle,
  HpFluentButtonTextAndStyle,
  HpFluentInputTextAndStyle,
  HpInputTextAndStyle,
  HpQuestionFormatTypes,
  HpStandardButtonTextAndStyle,
  HpStandardInputTextAndStyle
} from '@avantia/questionset-model';
import React, { FocusEvent, FormEvent, KeyboardEvent, MouseEvent } from 'react';
import { HpClientQuestion } from '../../types';
import {
  HpCommonButtonControl,
  HpCommonButtonControlProps,
  HpEventDetail,
  HpInputControl,
  HpInputControlProps,
  HpOnFocusEvent,
  HpOnFormEvent,
  HpOnKeyboardEvent,
  HpOnMouseEvent
} from '../../types/inputControlTypes';
import { HpGetControlFunction } from '../questionSetForm/questionSetPageFunctions';

export function createFocusEvent(
  event: HpOnFocusEvent | undefined,
  detail: HpEventDetail,
  fallbackEvent: HpOnFormEvent
): (event: React.FocusEvent<HTMLElement>) => void {
  if (event) {
    return (e): void => event(e, detail);
  }

  return fallbackEvent as any;
}

// 'Standard' as in not 'Fluent'.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createStandardMouseEvent(event: HpOnMouseEvent | undefined, question: HpClientQuestion) {
  return createMouseEvent(event, { type: 'standard', question });
}

export function createMouseEvent(
  event: HpOnMouseEvent | undefined,
  detail: HpEventDetail
): (event: MouseEvent<HTMLElement>) => void {
  return event ? (e): void => event(e, detail) : IgnoreClickEvent;
}

// 'Standard' as in not 'Fluent'.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createStandardFormEvent(event: HpOnFormEvent, question: HpClientQuestion) {
  return createFormEvent(event, { type: 'standard', question });
}

export function createFormEvent(event: HpOnFormEvent, detail: HpEventDetail): (event: FormEvent<HTMLElement>) => void {
  return (e): void => event(e, detail);
}

export function createKeyboardEvent(
  event: HpOnKeyboardEvent,
  detail: HpEventDetail
): (event: KeyboardEvent<HTMLElement>) => void {
  return (e): void => event(e, detail);
}

export function IgnoreBlurEvent(event: FocusEvent<HTMLElement>): void {
  event.preventDefault();
}

export function IgnoreClickEvent(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

export function IgnoreChangeEvent(event: FormEvent<HTMLElement>): void {
  event.preventDefault();
}

export function IgnoreOnKeyDownEvent(event: FormEvent<HTMLElement>): void {
  event.preventDefault();
}

export interface HpSelectControlToUseResponse {
  Control: HpInputControl<HpInputControlProps>;
  formatType: HpQuestionFormatTypes;
}

export interface HpSelectButtonControlToUseResponse {
  Control: HpCommonButtonControl<HpCommonButtonControlProps>;
  formatType: HpQuestionFormatTypes;
}

export function selectButtonControlToUse(
  questionFormats: HpQuestionFormatTypes[],
  getControl: HpGetControlFunction
): HpSelectButtonControlToUseResponse | undefined {
  return selectControlToUse(questionFormats, getControl) as any;
}

export function selectControlToUse(
  questionFormats: HpQuestionFormatTypes[],
  getControl: HpGetControlFunction
): HpSelectControlToUseResponse | undefined {
  const len = questionFormats.length;
  for (let i = 0; i < len; i++) {
    const formatType = questionFormats[i];
    const Control = getControl(formatType);
    if (Control) {
      return { Control, formatType };
    }
  }

  return undefined;
}

export function selectTextAndStyleDataToUse(
  questionFormats: HpQuestionFormatTypes[],
  textAndStyle: HpButtonTextAndStyle | HpInputTextAndStyle
): HpStandardButtonTextAndStyle | HpFluentButtonTextAndStyle | HpStandardInputTextAndStyle | HpFluentInputTextAndStyle {
  const len = questionFormats.length;
  for (let i = 0; i < len; i++) {
    const formatType = questionFormats[i];
    const data = textAndStyle[formatType];
    if (data) {
      return data;
    }
  }

  throw new HpError(
    `No format could be found for the control data. Data provided: ${JSON.stringify({ questionFormats, textAndStyle })}`
  );
}
